import { AuxiliumComponent } from "../auxilium-component";

export default class Textarea extends AuxiliumComponent {
	static observable = ["required", "label", "secondary-label", "helper-text", "description", "prefix-button", "suffix-button", "prefix-tooltip", "suffix-tooltip", "prefix-description", "suffix-description", "suffix-icon", "prefix-icon"];
	static observableBooleans = ["code-preview", "responsive", "readonly"];

	#Mappers = {};

	static Observer = new ResizeObserver(entries => entries.forEach(entry => Textarea.#ResizeTextArea(entry.target)));

	constructor() {
		super();

		this.InitializeValidationMessages();

		this.normalizeContent = false;

		this.#Mappers = {
			maxlength: this.getAttribute("maxlength"),
			minlength: this.getAttribute("minlength"),
			name: this.getAttribute("name"),
			spellcheck: this.getAttribute("spellcheck"),
			placeholder: this.getAttribute("placeholder"),
			readonly: this.readonly,
			required: this.hasAttribute("required"),
			rows: this.getAttribute("rows"),
			autofocus: this.hasAttribute("autofocus")
		};

		if (!this.#Mappers.spellcheck && this.codePreview) { this.#Mappers.spellcheck = true; }
		if (this.closest("form") && !this.#Mappers.name) { this.#Mappers.name = this.id ?? this.identifier; }

		this.removeAttribute("autofocus");

		Object.keys(this.#Mappers).forEach(key => this[key] = this.#Mappers[key]);

		this.classList.add("textarea__wrapper");
	}

	get selectionStart() {
		return this.querySelector("textarea")?.selectionStart;
	}

	get selectionEnd() {
		return this.querySelector("textarea")?.selectionEnd;
	}

	setSelectionRange(start, end, direction) {
		this.querySelector("textarea")?.setSelectionRange(start, end, direction);
	}

	get value() {
		return this.querySelector("textarea")?.value ?? this.textContent;
	}

	set value(val) {
		const textArea = this.querySelector("textarea");

		if (textArea) {
			textArea.value = val;
		}
	}

	parsedCallback() {
		const element = Object.assign(document.createElement("div"), {
			innerHTML:
				`
				<label class="input-field__label">${this.label}<span class="input-field__label--secondary">${this.secondaryLabel}</span></label>
				<div class="input__wrapper">
					<textarea class="input-field input-field__textarea">${this.innerHTML}</textarea>
				</div>
				<div class="input-field__helper-text">${this.helperText}</div>
				`.trim()
		}), textArea = element.querySelector("textarea");

		this.innerHTML = "";

		textArea.id = this.identifier;

		textArea.onkeyup = () => this.raiseEvent("keyup", "selectionchange");
		textArea.onselect = () => this.raiseEvent("select", "selectionchange");
		textArea.onclick = () => this.raiseEvent("selectionchange");
		textArea.oninput = () => this.raiseEvent("input", "selectionchange");

		textArea.onchange = ev => {
			ev.preventDefault();
			ev.stopPropagation();

			this.reportValidity();
			this.closest("chip-form")?.checkValidity();

			this.raiseEvent("change");
		}

		if (!this.label && !this.secondaryLabel) { element.querySelector("label").remove(); }
		else { element.querySelector("label").htmlFor = this.identifier; }

		if (!this.secondaryLabel) { element.querySelector(".input-field__label--secondary")?.remove(); }
		if (!this.helperText) { element.querySelector(".input-field__helper-text").remove(); }
		if (this.codePreview) { textArea.classList.add("code-preview"); }

		if (this.prefixButton) {
			textArea.insertAdjacentElement("beforebegin", document.createElementWithContents("chip-button", null, {
				className: "input__bound",
				buttonStyle: "icon",
				icon: this.prefixButton,
				variation: "body",
				onclick: () => this.raiseEvent("prefixclick")
			}));
		} else if (this.prefixIcon) {
			textArea.insertAdjacentElement("beforebegin", document.createElementWithContents("span", document.createElementWithContents("chip-icon", null, { icon: this.prefixIcon }), { className: "input__bound" }));
		}

		if (this.suffixButton) {
			textArea.insertAdjacentElement("afterend", document.createElementWithContents("chip-button", null, {
				className: "input__bound",
				buttonStyle: "icon",
				icon: this.suffixButton,
				variation: "body",
				onclick: () => this.raiseEvent("suffixclick")
			}));
		} else if (this.suffixIcon) {
			textArea.insertAdjacentElement("afterend", document.createElementWithContents("span", document.createElementWithContents("chip-icon", null, { icon: this.suffixIcon }), { className: "input__bound" }));
		}

		if (this.prefixTooltip && textArea.previousElementSibling) { textArea.previousElementSibling.tooltip = this.prefixTooltip; }
		if (this.suffixTooltip && textArea.nextElementSibling) { textArea.nextElementSibling.tooltip = this.suffixTooltip; }

		if (this.prefixDescription && textArea.previousElementSibling) { textArea.previousElementSibling.description = this.prefixDescription; }
		if (this.suffixDescription && textArea.nextElementSibling) { textArea.nextElementSibling.description = this.suffixDescription; }

		if (this.responsive) {
			Textarea.Observer.observe(textArea);

			textArea.addEventListener("input", () => Textarea.#ResizeTextArea(textArea));
		}

		Object.keys(this.#Mappers).filter(x => this.#Mappers[x])
			.forEach(key => textArea.setAttribute(key, this.#Mappers[key] === true ? "" : this.#Mappers[key]));

		if (this.description) { textArea.ariaLabel = this.description; }
		else if (!this.label && this.#Mappers.placeholder) { textArea.ariaLabel = this.#Mappers.placeholder; }

		this.append(...element.childNodes);
	}

	focus() {
		this.querySelector("textarea")?.focus();
	}

	checkValidity() {
		const input = this.querySelector("textarea"),
			isValid = input?.checkValidity() ?? false;

		this.validationMessage = this.GetValidationMessage(input);

		return isValid;
	}

	setInvalid(message) {
		this.querySelector(".input-field__validation-error")?.remove();

		const input = this.querySelector("textarea"),
			msgElement = document.createElementWithContents("div", message, { className: "input-field__validation-error" });

		if (!this.disableMessages) {
			if (Browser.GetFlavour() === "gov") {
				this.querySelector(".input__wrapper").insertAdjacentElement("beforebegin", msgElement);
			} else {
				this.appendChild(msgElement);
			}
		}

		input.classList.add("input-field__invalid");

		this.querySelectorAll(".input__bound").forEach(x => x.classList.add("input-field__invalid"));
	}

	reportValidity() {
		const validity = this.checkValidity();

		this.querySelector(".input-field__validation-error")?.remove();
		this.querySelectorAll(".input-field__invalid").forEach(x => x.removeClass("input-field__invalid"));

		if (!validity) {
			this.setInvalid(this.validationMessage);
		}

		return validity;
	}

	attributeChanged(name, _, newValue) {
		switch (name) {
			case "required":
				{
					const textarea = this.querySelector("textarea");

					if (textarea) {
						textarea.required = this.IsAttributeValueTrue(newValue);
					}
				}
				break;
			case "readonly":
				this.querySelector("textarea").readOnly = this.IsAttributeValueTrue(newValue);
				break;
		}
	}

	static #ResizeTextArea(textArea) {
		const computed = window.getComputedStyle(textArea),
			component = textArea.closest("chip-textarea");

		let attempt = textArea;
		let scrollPreset = 0;

		while (scrollPreset === 0 && (attempt = attempt.parentElement)) {
			scrollPreset = attempt.scrollTop;
		}

		textArea.style.height = "0px";
		component.removeAttribute("style");

		component.style.setProperty("--input-height", `${textArea.scrollHeight + parseFloat(computed.borderBottomWidth) + parseFloat(computed.borderTopWidth)}px`);
		textArea.removeAttribute("style");
	}
}
